import { ChevronRight, Download, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, errMsg, formatDate, money } from "@/lib/api";
import { StatusBadge } from "./Dashboard";

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/admin/settings").then((r) => setStatuses(r.data.order_statuses || [])).catch(() => {});
  }, []);

  const load = useCallback(
    (silent) => {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      return API.get("/admin/orders", { params })
        .then((r) => setOrders(r.data))
        .catch((e) => { if (!silent) toast.error(errMsg(e)); });
    },
    [status, q, dateFrom, dateTo],
  );

  useEffect(() => {
    const t = setTimeout(() => load(false), 250);
    return () => clearTimeout(t);
  }, [load]);

  // Live auto-refresh so new orders / status changes appear without a manual reload.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") load(true); };
    const timer = setInterval(refresh, 5000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [load]);

  const quickAdvance = async (e, o) => {
    e.stopPropagation();
    try {
      const r = await API.post(`/admin/orders/${o.id}/advance`);
      toast.success(`${o.order_number} → ${r.data.status}`);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status: r.data.status } : x)));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await API.get("/admin/orders/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Orders exported to CSV");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl tracking-tight">Orders</h1>
        <Button
          variant="outline"
          onClick={exportCsv}
          disabled={exporting || !orders || orders.length === 0}
          className="rounded-full"
          data-testid="orders-export-csv-btn"
        >
          <Download className="mr-2 h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search order number or customer…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="orders-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48" data-testid="orders-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="filter-all-statuses">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s} data-testid={`filter-status-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full sm:w-40" data-testid="orders-date-from" aria-label="From date" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full sm:w-40" data-testid="orders-date-to" aria-label="To date" />
      </div>

      {!orders ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : orders.length === 0 ? (
        <p className="text-muted-foreground text-center py-16 border rounded-xl" data-testid="orders-empty">No orders match your filters.</p>
      ) : (
        <div className="border rounded-xl overflow-x-auto bg-card" data-testid="orders-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="hidden md:table-cell">Date</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/admin/orders/${o.id}`)} data-testid={`order-row-${o.order_number}`}>
                  <TableCell className="font-medium">{o.order_number}</TableCell>
                  <TableCell>{o.customer?.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{formatDate(o.created_at)}</TableCell>
                  <TableCell className="font-semibold">{money(o.total)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{o.payment_status}</TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell>
                    {!["Completed", "Cancelled"].includes(o.status) && (
                      <button
                        onClick={(e) => quickAdvance(e, o)}
                        className="p-1.5 rounded-full border hover:bg-primary hover:text-primary-foreground transition-colors"
                        title="Move to next status"
                        data-testid={`advance-order-${o.order_number}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
